<template>
  <div :class="$style.container">
    <!-- 헤더 -->
    <div :class="$style.header">
      <div :class="$style.headerLeft">
        <button :class="$style.backButton" @click="goBack" title="프로필로 돌아가기">←</button>
        <h2 :class="$style.title">알림</h2>
      </div>
      <button v-if="!loading && notifications.length > 0 && unreadCount > 0" :class="$style.markAllButton"
        @click="markAllAsRead" :disabled="markingAll">
        {{ markingAll ? '처리 중...' : '전체 확인' }}
      </button>
      <div v-else :class="$style.spacer"></div>
    </div>

    <div :class="$style.paper">
      <!-- 로딩 상태 -->
      <div v-if="loading" :class="$style.loading">
        <div :class="$style.spinner"></div>
        <p>알림을 불러오는 중...</p>
      </div>

      <!-- 알림 목록 -->
      <div v-else-if="notifications.length > 0" :class="$style.notificationList">
        <div v-for="notification in notifications" :key="notification.id"
          :class="[$style.notificationItem, { [$style.unread]: !notification.read }]"
          @click="handleNotificationClick(notification)">
          <div :class="$style.notificationIcon">
            <img v-if="notification.read" src="@/assets/images/alarms_lightgray.png" alt="알림" :class="$style.icon" />
            <img v-else src="@/assets/images/alarms.png" alt="알림" :class="$style.icon" />
          </div>
          <div :class="$style.notificationContent">
            <div :class="$style.notificationTitle">{{ notification.title }}</div>
            <div :class="$style.notificationMessage">
              {{ notification.message }}
            </div>
            <div :class="$style.notificationDate">
              {{ formatDate(notification.createdAt) }}
            </div>
          </div>
          <div v-if="!notification.read" :class="$style.unreadBadge"></div>
        </div>
      </div>

      <!-- 알림 없음 -->
      <div v-else :class="$style.emptyState">
        <div :class="$style.emptyIcon">🔔</div>
        <h3>알림이 없습니다</h3>
        <p>새로운 알림이 오면 여기에 표시됩니다.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { collection, query, where, getDocs, orderBy, doc, getDoc } from "firebase/firestore";
import { firestore } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import { markNotificationAsRead } from "@/utils/write/user";
import { trackActivity, ACTIVITY_TYPES } from "@/utils/activityTracker";

export default {
  name: "MyNotifications",
  data() {
    return {
      notifications: [],
      loading: true,
      markingAll: false,
      userStore: useUserStore(),
    };
  },
  computed: {
    unreadCount() {
      return this.notifications.filter(n => !n.read).length;
    },
  },
  async mounted() {
    if (!this.userStore.isLogin) {
      this.$alert("로그인이 필요합니다.");
      this.$router.push("/");
      return;
    }
    await this.loadNotifications();
  },
  methods: {
    async getArtistRouteSlug(artistIdentifier) {
      if (artistIdentifier == null) return null;

      const raw = String(artistIdentifier);
      const numeric = Number(raw);
      if (Number.isFinite(numeric)) {
        return raw;
      }

      try {
        const artistDocRef = doc(firestore, "artists", raw);
        const artistDocSnap = await getDoc(artistDocRef);
        if (!artistDocSnap.exists()) return null;

        const artistData = artistDocSnap.data();
        if (artistData && artistData.slug != null) {
          return String(artistData.slug);
        }
      } catch (error) {
        console.warn("알림 아티스트 slug 조회 실패:", error);
      }

      return null;
    },

    async loadNotifications() {
      try {
        this.loading = true;
        const notificationsRef = collection(firestore, "alarms");
        const q = query(
          notificationsRef,
          where("userId", "==", this.userStore.email),
          orderBy("createdAt", "desc")
        );

        const querySnapshot = await getDocs(q);
        this.notifications = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error("알림 로드 오류:", error);
        this.$alert("알림을 불러오는 중 오류가 발생했습니다.");
      } finally {
        this.loading = false;
      }
    },

    async handleNotificationClick(notification) {
      // 알림을 읽음 처리
      if (!notification.read) {
        try {
          await markNotificationAsRead(notification.id);
          notification.read = true;
        } catch (error) {
          console.error("알림 읽음 처리 오류:", error);
        }
      }

      // 알림 클릭 활동 기록
      trackActivity(ACTIVITY_TYPES.NOTIFICATION_CLICK, {
        target: notification.type,
        targetId:
          notification.liveId ||
          notification.boardId ||
          notification.commissionId,
        metadata: { notificationId: notification.id },
      });

      // 알림 타입에 따라 적절한 페이지로 이동
      if (notification.type === "comment" && notification.boardId) {
        this.$router.push(`/board/${notification.boardId}`);
      } else if (
        (notification.type === "live" || notification.type === "reminder") &&
        notification.liveId
      ) {
        this.$router.push(`/live/${notification.liveId}`);
      } else if (notification.type === "commission" && notification.commissionId) {
        this.$router.push(`/commission/${notification.commissionId}`);
      } else if (
        (notification.type === "VIDEO_LIKE" || notification.targetType === "video") &&
        (notification.targetId || notification.videoId)
      ) {
        this.$router.push(`/video/${notification.targetId || notification.videoId}`);
      } else if (
        (notification.type === "POST_LIKE" || notification.targetType === "board") &&
        (notification.targetId || notification.boardId)
      ) {
        this.$router.push(`/board/${notification.targetId || notification.boardId}`);
      } else if (
        (notification.type === "ARTIST_LIKE" || notification.targetType === "artist") &&
        (notification.targetId || notification.artistId)
      ) {
        const routeSlug = await this.getArtistRouteSlug(
          notification.targetId || notification.artistId
        );
        if (routeSlug) {
          this.$router.push(`/artist/${routeSlug}`);
        }
      }
    },

    formatDate(date) {
      if (!date) return "";
      const d = date.toDate ? date.toDate() : new Date(date);
      const now = new Date();
      const diff = now - d;

      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return "방금 전";
      if (minutes < 60) return `${minutes}분 전`;
      if (hours < 24) return `${hours}시간 전`;
      if (days < 7) return `${days}일 전`;

      return d.toLocaleDateString("ko-KR");
    },

    async markAllAsRead() {
      try {
        this.markingAll = true;

        // 읽지 않은 알림들 필터링
        const unreadNotifications = this.notifications.filter(n => !n.read);

        if (unreadNotifications.length === 0) {
          return;
        }

        // 모든 읽지 않은 알림을 읽음 처리
        await Promise.all(
          unreadNotifications.map(notification =>
            markNotificationAsRead(notification.id)
          )
        );

        // 로컬 상태 업데이트
        unreadNotifications.forEach(notification => {
          notification.read = true;
        });

        // 전체 확인 활동 기록
        trackActivity(ACTIVITY_TYPES.NOTIFICATION_READ_ALL, {
          metadata: { count: unreadNotifications.length },
        });
      } catch (error) {
        console.error("전체 읽음 처리 오류:", error);
        this.$alert("알림을 읽음 처리하는 중 오류가 발생했습니다.");
      } finally {
        this.markingAll = false;
      }
    },

    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style module>
/* Medium: 481px ~ (기본 스타일, 최대 1024px) */
.container {
  width: 90%;
  min-height: calc(100vh - 80px);
  margin: 0 auto;
}

.paper {
  background: white;
  padding: 8px;
  margin-bottom: 20px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 6px 0 4px;
  border-bottom: 1px solid #efefef;
  flex-shrink: 0;
}

.headerLeft {
  display: flex;
  align-items: center;
  gap: 4px;
}

.backButton {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #333;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: background 0.15s;
}

.backButton:hover {
  background: #f5f5f5;
}

.title {
  font-size: 19px;
  font-weight: 700;
  color: #111;
  margin: 0;
  font-family: "NanumSquareRoundEB", sans-serif;
  letter-spacing: -0.3px;
}

.spacer {
  width: 30px;
}

.markAllButton {
  padding: 6px 12px;
  background: linear-gradient(135deg, #66bdff 0%, #5aa3e8 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(102, 189, 255, 0.3);
  font-family: "NanumSquareRoundR", sans-serif;
  white-space: nowrap;
}

.markAllButton:hover:not(:disabled) {
  background: linear-gradient(135deg, #5aa3e8 0%, #4a8ed1 100%);
  box-shadow: 0 4px 8px rgba(102, 189, 255, 0.4);
  transform: translateY(-1px);
}

.markAllButton:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(102, 189, 255, 0.3);
}

.markAllButton:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6bbeb8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.notificationList {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.notificationItem {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafafa;
  border-left: 4px solid transparent;
  position: relative;
}

.notificationItem:hover {
  background: #f0f0f0;
  transform: translateX(2px);
}

.notificationItem.unread {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-left-color: #2196f3;
}

.notificationItem.unread:hover {
  background: linear-gradient(135deg, #c5d9f0 0%, #a1c7e8 100%);
}

.notificationIcon {
  margin-right: 15px;
  flex-shrink: 0;
}

.icon {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.notificationContent {
  flex: 1;
}

.notificationTitle {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.notificationMessage {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
  line-height: 1.5;
}

.notificationDate {
  font-size: 11px;
  color: #999;
}

.unreadBadge {
  width: 10px;
  height: 10px;
  background: #ff4444;
  border-radius: 50%;
  margin-left: 10px;
  flex-shrink: 0;
  margin-top: 5px;
}

.emptyState {
  text-align: center;
  padding: 50px 20px;
}

.emptyIcon {
  font-size: 56px;
  margin-bottom: 16px;
}

.emptyState h3 {
  font-size: 18px;
  color: #333;
  margin-bottom: 10px;
}

.emptyState p {
  font-size: 13px;
  color: #666;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media screen and (max-width: 480px) {
  .container {
    width: 100%;
    padding: 0;
  }

  .paper {
    padding: 8px;
    margin-bottom: 15px;
  }


  .markAllButton {
    font-size: 12px;
    padding: 6px 10px;
  }

  .spacer {
    width: 28px;
  }

  .notificationList {
    gap: 6px;
  }

  .notificationItem {
    padding: 10px;
    border-radius: 6px;
  }

  .notificationItem:hover {
    transform: translateX(1px);
  }

  .notificationTitle {
    font-size: 14px;
    margin-bottom: 4px;
  }

  .notificationMessage {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .notificationDate {
    font-size: 10px;
  }

  .unreadBadge {
    width: 8px;
    height: 8px;
    margin-top: 4px;
  }

  .notificationIcon {
    margin-right: 10px;
  }

  .icon {
    width: 32px;
    height: 32px;
  }

  .emptyState {
    padding: 40px 15px;
  }

  .emptyIcon {
    font-size: 48px;
    margin-bottom: 12px;
  }

  .emptyState h3 {
    font-size: 16px;
    margin-bottom: 8px;
  }

  .emptyState p {
    font-size: 12px;
  }

  .loading {
    padding: 40px 15px;
  }

  .spinner {
    width: 36px;
    height: 36px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #6bbeb8;
  }
}
</style>
